import { api } from './client';
import type { CustomerDTO } from './types/customer';
import type { BeneficiaryVO } from './types/order';
import type { RequestConfig } from '../request';

export interface CustomerListRequest {
  pageNum?: number;
  pageSize?: number;
  name?: string;
  mobile?: string;
  [key: string]: any;
}

export interface Response {
  content?: CustomerDTO[];
  page?: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
  empty?: boolean;
  first?: boolean;
  last?: boolean;
  number?: number;
  numberOfElements?: number;
  pageable?: Pageable;
  size?: number;
  sort?: Sort[];
  total?: number;
  totalElements?: number;
  totalPages?: number;
  [property: string]: any;
}

export interface Pageable {
  pageNumber?: number;
  pageSize?: number;
  [property: string]: any;
}

export interface Sort {
  direction?: "ASC" | "DESC";
  property?: string;
  [property: string]: any;
}

/**
 * 创建客户
 * POST /api/v1/customer/create
 */
export function createCustomer(data: CustomerDTO): Promise<number> {
  return api.post<number>('/customer/create', data);
}

/**
 * 查询客户列表
 * GET /api/v1/customer/list
 */
export function getCustomerList(params: CustomerListRequest): Promise<Response> {
  return api.get<Response>('/customer/list', { params });
}

/**
 * 查询客户详情
 * GET /api/v1/customer/detail
 */
export function getCustomerDetail(
  customerId: number | string,
  config?: RequestConfig
): Promise<CustomerDTO> {
  return api.get<CustomerDTO>('/customer/detail', {
    ...config,
    params: { customerId },
  });
}

/**
 * 删除客户受保人
 * PUT /api/v1/customer/delete-insured
 */
export function deleteInsured(customerId: number, insuredId: number): Promise<void> {
  return api.put<void>('/customer/delete-insured', null, {
    params: { customerId, insuredId },
  });
}

/**
 * 删除客户受益人
 * PUT /api/v1/customer/delete-beneficiary
 */
export function deleteCustomerBeneficiary(customerId: number, beneficiaryId: number): Promise<void> {
  return api.put<void>('/customer/delete-beneficiary', null, {
    params: { customerId, beneficiaryId },
  });
}

/**
 * 更新客户受益人信息请求参数
 */
export interface UpdateCustomerBeneficiaryRequest {
  /**
   * 客户ID
   */
  customerId: number;
  /**
   * 受益人ID
   */
  beneficiaryId: number;
  /**
   * 受益人基本信息
   */
  basic?: BeneficiaryVO['basic'];
  /**
   * 受益人和受保人的关系
   */
  relationship?: BeneficiaryVO['relationship'];
  /**
   * 受益比例
   */
  percentage?: number;
  /**
   * 备注/说明
   */
  remark?: string;
  [property: string]: any;
}

/**
 * 更新客户受益人信息
 * PUT /api/v1/customer/update-beneficiary
 */
export function updateCustomerBeneficiary(
  customerId: number,
  beneficiaryId: number,
  data: BeneficiaryVO
): Promise<void> {
  const requestBody: UpdateCustomerBeneficiaryRequest = {
    customerId,
    beneficiaryId,
    basic: data.basic,
    relationship: data.relationship,
    percentage: data.percentage,
    remark: (data as any).remark,
  };
  return api.put<void>('/customer/update-beneficiary', requestBody);
}

/**
 * 更新客户受保人信息请求参数
 */
export interface UpdateCustomerInsuredRequest {
  /**
   * 客户ID
   */
  customerId: number;
  /**
   * 受保人ID
   */
  insuredId: number;
  /**
   * 受保人基本信息
   */
  basic?: InsuredDTO['basic'];
  /**
   * 受保人个人信息
   */
  personal?: InsuredDTO['personal'];
  /**
   * 受保人工作信息
   */
  work?: InsuredDTO['work'];
  /**
   * 受保人税务信息
   */
  tax?: InsuredDTO['tax'];
  /**
   * 受保人与投保人关系
   */
  relationship?: InsuredDTO['relationship'];
  /**
   * 备注/说明
   */
  remark?: string;
  [property: string]: any;
}

/**
 * 更新客户受保人信息
 * PUT /api/v1/customer/update-insured
 */
export function updateCustomerInsured(
  customerId: number,
  insuredId: number,
  data: InsuredDTO
): Promise<void> {
  const requestBody: UpdateCustomerInsuredRequest = {
    customerId,
    insuredId,
    basic: data.basic,
    personal: data.personal,
    work: data.work,
    tax: data.tax,
    relationship: data.relationship,
    remark: (data as any).remark,
  };
  return api.put<void>('/customer/update-insured', requestBody);
}
